"use client"
import { Modal, ModalContent } from "@heroui/react";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { track } from "@/lib/log";

interface ImagePreviewProps {
    src: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    isMobile?: boolean;
}

const ImagePreview = ({ src, isOpen, onOpenChange, isMobile = false }: ImagePreviewProps) => {
    useEffect(() => {
        if (isOpen) {
            track("image_preview", {
                action: "open",
                src: src,
                type: "click",
                page: "detail"
            });
        }
    }, [isOpen, src]);

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        const img = new window.Image();
        img.src = src;
        img.onload = () => setIsLoading(false);
        img.onerror = () => setIsLoading(false);
        if (img.complete) setIsLoading(false);
    }, [src]);

    if (!isOpen) return null;

    if (isMobile) {
        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
                tabIndex={0}
                aria-label="image preview"
                onClick={() => onOpenChange(false)}
                onKeyDown={e => {
                    if (e.key === "Escape") onOpenChange(false);
                }}
            >
                <div
                    className="absolute top-4 right-4"
                    tabIndex={0}
                    aria-label="close image preview"
                    onClick={e => {
                        e.stopPropagation();
                        onOpenChange(false);
                    }}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            onOpenChange(false);
                        }
                    }}
                    role="button"
                >
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-black/60 text-white text-2xl cursor-pointer select-none">
                        ×
                    </span>
                </div>
                <div className="w-full h-full flex items-center justify-center">
                    <img
                        src={src}
                        alt="image"
                        className="max-w-full max-h-full object-contain w-full h-full"
                        draggable={false}
                    />
                </div>
            </div>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            backdrop="opaque"
            size="5xl"
            classNames={{
                closeButton: clsx(
                    "text-white bg-black/60 hover:bg-black/70 hover:scale-105 transition-all duration-300",
                    isLoading && "hidden"
                ),
            }}
        >
            <ModalContent className="p-[50px] border-none rounded-none bg-transparent shadow-none">
                <div className="w-full h-full flex items-center justify-center">
                    <img src={src} alt="image" className="w-full h-full max-w-[100vw] max-h-[100vh] object-contain" />
                </div>
            </ModalContent>
        </Modal>
    );
};

export default ImagePreview;